import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc as fsDoc,
  query, where, orderBy
} from 'firebase/firestore';
import {
  ref as rtRef, get as rtGet, set as rtSet, update as rtUpdate,
  remove as rtRemove, push as rtPush
} from 'firebase/database';
import {
  ref as storageRef, listAll, deleteObject, getDownloadURL,
  uploadBytes, uploadString
} from 'firebase/storage';
import { db, database, storage } from '../../../firebase';
import Preloader from '../../Preloader';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

const ModifyDataTab = () => {
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [newItem, setNewItem] = useState(null);
  const [referenceData, setReferenceData] = useState({});
  const [bulkSelection, setBulkSelection] = useState([]);
  const [activeTab, setActiveTab] = useState('browse');
  const [fileUpload, setFileUpload] = useState(null);

  // Resource definitions with metadata for better display
  const resourceDefinitions = {
    firestore: {
      users: {
        displayName: 'Users',
        fields: {
          firstName: { type: 'string', label: 'First Name' },
          lastName: { type: 'string', label: 'Last Name' },
          email: { type: 'string', label: 'Email' },
          role: { type: 'string', label: 'Role', options: ['student', 'professor', 'admin'] },
          universityId: { type: 'string', label: 'University ID' },
          institution: { type: 'string', label: 'Institution' }
        }
      },
      classes: {
        displayName: 'Classes',
        fields: {
          class_name: { type: 'string', label: 'Class Name' },
          professor_uid: { 
            type: 'reference', 
            label: 'Professor', 
            collection: 'users',
            filter: ['role', '==', 'professor'],
            displayField: 'firstName'
          },
          start_date: { type: 'timestamp', label: 'Start Date' },
          end_date: { type: 'timestamp', label: 'End Date' }
        }
      },
      enrollments: {
        displayName: 'Enrollments',
        fields: {
          classId: { 
            type: 'reference', 
            label: 'Class', 
            collection: 'classes',
            displayField: 'class_name'
          },
          studentId: { 
            type: 'reference', 
            label: 'Student', 
            collection: 'users',
            filter: ['role', '==', 'student'],
            displayField: 'firstName'
          }
        }
      },
      grades: {
        displayName: 'Grades',
        fields: {
          classId: { 
            type: 'reference', 
            label: 'Class', 
            collection: 'classes',
            displayField: 'class_name'
          },
          studentId: { 
            type: 'reference', 
            label: 'Student', 
            collection: 'users',
            filter: ['role', '==', 'student'],
            displayField: 'firstName'
          },
          points: { type: 'object', label: 'Points' }
        }
      },
      tickets: {
        displayName: 'Tickets',
        fields: {
          title: { type: 'string', label: 'Title' },
          description: { type: 'string', label: 'Description' },
          status: { type: 'string', label: 'Status', options: ['open', 'in-progress', 'resolved'] },
          createdBy: { 
            type: 'reference', 
            label: 'Created By', 
            collection: 'users',
            displayField: 'firstName'
          }
        }
      }
    },
    realtime: {
      sessions: {
        displayName: 'Sessions',
        fields: {
          class_id: { 
            type: 'reference', 
            label: 'Class', 
            collection: 'classes',
            displayField: 'class_name'
          },
          start_date_time: { type: 'timestamp', label: 'Start Time' },
          end_date_time: { type: 'timestamp', label: 'End Time' }
        }
      },
      attendance: {
        displayName: 'Attendance',
        fields: {
          class_id: { 
            type: 'reference', 
            label: 'Class', 
            collection: 'classes',
            displayField: 'class_name'
          },
          session_Id: { 
            type: 'reference', 
            label: 'Session', 
            collection: 'sessions',
            displayField: 'start_date_time',
            path: 'realtime/sessions'
          },
          userIds: { 
            type: 'array', 
            label: 'Students Present',
            itemType: {
              type: 'reference',
              collection: 'users',
              displayField: 'firstName'
            }
          }
        }
      },
      answers: {
        displayName: 'Answers',
        fields: {
          class_id: { 
            type: 'reference', 
            label: 'Class', 
            collection: 'classes',
            displayField: 'class_name'
          },
          session_Id: { 
            type: 'reference', 
            label: 'Session', 
            collection: 'sessions',
            displayField: 'start_date_time',
            path: 'realtime/sessions'
          },
          responses: {
            type: 'array',
            label: 'Responses',
            itemType: {
              type: 'object',
              fields: {
                userId: {
                  type: 'reference',
                  collection: 'users',
                  displayField: 'firstName'
                },
                answer: { type: 'string', label: 'Answer' },
                timestamp: { type: 'timestamp', label: 'Time' }
              }
            }
          }
        }
      }
    },
    storage: {
      questions: {
        displayName: 'Question Images',
        fields: {
          name: { type: 'string', label: 'Filename' },
          url: { type: 'string', label: 'URL' }
        }
      }
    }
  };

  // Initialize resources with metadata
  useEffect(() => {
    const res = [];
    
    Object.keys(resourceDefinitions.firestore).forEach(name => {
      res.push({ 
        type: 'firestore', 
        name,
        displayName: resourceDefinitions.firestore[name].displayName,
        fields: resourceDefinitions.firestore[name].fields
      });
    });
    
    Object.keys(resourceDefinitions.realtime).forEach(name => {
      res.push({ 
        type: 'realtime', 
        name,
        displayName: resourceDefinitions.realtime[name].displayName,
        fields: resourceDefinitions.realtime[name].fields
      });
    });
    
    Object.keys(resourceDefinitions.storage).forEach(name => {
      res.push({ 
        type: 'storage', 
        name,
        displayName: resourceDefinitions.storage[name].displayName,
        fields: resourceDefinitions.storage[name].fields
      });
    });
    
    setResources(res);
  }, []);

  // Load reference data for dropdowns
  useEffect(() => {
    const loadReferenceData = async () => {
      const refs = {};
      
      // Load users
      const usersSnap = await getDocs(collection(db, 'users'));
      refs.users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Load classes
      const classesSnap = await getDocs(collection(db, 'classes'));
      refs.classes = classesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Load sessions from Realtime DB
      const sessionsSnap = await rtGet(rtRef(database, 'sessions'));
      const sessionsData = sessionsSnap.exists() ? sessionsSnap.val() : {};
      refs.sessions = Object.entries(sessionsData).map(([id, data]) => ({ id, ...data }));
      
      setReferenceData(refs);
    };
    
    loadReferenceData();
  }, []);

  // Fetch items when selection changes
  useEffect(() => {
    if (!selectedResource) return;
    
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      setActiveTab('browse');
      setEditItem(null);
      setNewItem(null);
      setBulkSelection([]);

      try {
        if (selectedResource.type === 'firestore') {
          const snap = await getDocs(collection(db, selectedResource.name));
          setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } else if (selectedResource.type === 'realtime') {
          const snap = await rtGet(rtRef(database, selectedResource.name));
          const data = snap.exists() ? snap.val() : {};
          // For nested structures, we flatten with paths
          if (selectedResource.name === 'sessions' || selectedResource.name === 'attendance' || selectedResource.name === 'answers') {
            const flattened = [];
            for (const classId in data) {
              for (const sessionId in data[classId]) {
                flattened.push({
                  id: `${classId}/${sessionId}`,
                  class_id: classId,
                  session_Id: sessionId,
                  ...data[classId][sessionId]
                });
              }
            }
            setItems(flattened);
          } else {
            setItems(Object.entries(data).map(([key, value]) => ({ id: key, ...value })));
          }
        } else if (selectedResource.type === 'storage') {
          const listRef = storageRef(storage, selectedResource.name);
          const listing = await listAll(listRef);
          const itemsWithUrls = await Promise.all(
            listing.items.map(async item => {
              const url = await getDownloadURL(item);
              return { 
                id: item.fullPath, 
                name: item.name, 
                url,
                ref: item 
              };
            })
          );
          setItems(itemsWithUrls);
        }
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [selectedResource]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    
    return items.filter(item => {
      // Search across all fields for the term
      return Object.values(item).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value).toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });
    });
  }, [items, searchTerm]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete this ${selectedResource.displayName} record?`)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (selectedResource.type === 'firestore') {
        await deleteDoc(fsDoc(db, selectedResource.name, item.id));
      } else if (selectedResource.type === 'realtime') {
        // Handle nested paths for sessions/attendance/answers
        if (selectedResource.name === 'sessions' || selectedResource.name === 'attendance' || selectedResource.name === 'answers') {
          const [classId, sessionId] = item.id.split('/');
          await rtRemove(rtRef(database, `${selectedResource.name}/${classId}/${sessionId}`));
        } else {
          await rtRemove(rtRef(database, `${selectedResource.name}/${item.id}`));
        }
      } else if (selectedResource.type === 'storage') {
        await deleteObject(item.ref);
      }
      
      // Refresh data
      setSelectedResource({ ...selectedResource });
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkSelection.length || !window.confirm(`Are you sure you want to delete ${bulkSelection.length} records?`)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      for (const id of bulkSelection) {
        const item = items.find(i => i.id === id);
        if (item) {
          if (selectedResource.type === 'firestore') {
            await deleteDoc(fsDoc(db, selectedResource.name, id));
          } else if (selectedResource.type === 'realtime') {
            if (selectedResource.name === 'sessions' || selectedResource.name === 'attendance' || selectedResource.name === 'answers') {
              const [classId, sessionId] = id.split('/');
              await rtRemove(rtRef(database, `${selectedResource.name}/${classId}/${sessionId}`));
            } else {
              await rtRemove(rtRef(database, `${selectedResource.name}/${id}`));
            }
          } else if (selectedResource.type === 'storage') {
            await deleteObject(item.ref);
          }
        }
      }
      
      // Refresh data
      setSelectedResource({ ...selectedResource });
      setBulkSelection([]);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditItem(JSON.parse(JSON.stringify(item)));
    setActiveTab('edit');
  };

  const handleCreate = () => {
    const emptyItem = {};
    if (selectedResource.fields) {
      Object.keys(selectedResource.fields).forEach(field => {
        const fieldDef = selectedResource.fields[field];
        if (fieldDef.type === 'array') {
          emptyItem[field] = [];
        } else if (fieldDef.type === 'object') {
          emptyItem[field] = {};
        } else {
          emptyItem[field] = '';
        }
      });
    }
    setNewItem(emptyItem);
    setActiveTab('create');
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (selectedResource.type === 'firestore') {
        const itemRef = fsDoc(db, selectedResource.name, editItem.id);
        await updateDoc(itemRef, editItem);
      } else if (selectedResource.type === 'realtime') {
        if (selectedResource.name === 'sessions' || selectedResource.name === 'attendance' || selectedResource.name === 'answers') {
          const [classId, sessionId] = editItem.id.split('/');
          await rtUpdate(rtRef(database, `${selectedResource.name}/${classId}/${sessionId}`), editItem);
        } else {
          await rtUpdate(rtRef(database, `${selectedResource.name}/${editItem.id}`), editItem);
        }
      } else if (selectedResource.type === 'storage') {
        // Storage items can't be edited, only replaced
        setError('Storage items cannot be edited directly. Delete and upload a new version.');
      }
      
      // Refresh data
      setSelectedResource({ ...selectedResource });
      setEditItem(null);
      setActiveTab('browse');
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNew = async () => {
    if (!newItem) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (selectedResource.type === 'firestore') {
        await addDoc(collection(db, selectedResource.name), newItem);
      } else if (selectedResource.type === 'realtime') {
        if (selectedResource.name === 'sessions' || selectedResource.name === 'attendance' || selectedResource.name === 'answers') {
          // For nested structures, we need class_id and session_Id
          if (!newItem.class_id || !newItem.session_Id) {
            throw new Error('class_id and session_Id are required for this resource');
          }
          await rtSet(rtRef(database, `${selectedResource.name}/${newItem.class_id}/${newItem.session_Id}`), newItem);
        } else {
          const newRef = rtPush(rtRef(database, selectedResource.name));
          await rtSet(newRef, newItem);
        }
      } else if (selectedResource.type === 'storage') {
        if (!fileUpload) {
          throw new Error('Please select a file to upload');
        }
        const fileRef = storageRef(storage, `${selectedResource.name}/${fileUpload.name}`);
        await uploadBytes(fileRef, fileUpload);
      }
      
      // Refresh data
      setSelectedResource({ ...selectedResource });
      setNewItem(null);
      setFileUpload(null);
      setActiveTab('browse');
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value, isNew = false) => {
    if (isNew) {
      setNewItem(prev => ({ ...prev, [field]: value }));
    } else {
      setEditItem(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleArrayFieldChange = (field, index, value, isNew = false) => {
    if (isNew) {
      setNewItem(prev => {
        const newArray = [...prev[field]];
        newArray[index] = value;
        return { ...prev, [field]: newArray };
      });
    } else {
      setEditItem(prev => {
        const newArray = [...prev[field]];
        newArray[index] = value;
        return { ...prev, [field]: newArray };
      });
    }
  };

  const handleAddArrayItem = (field, isNew = false) => {
    if (isNew) {
      setNewItem(prev => {
        const newArray = prev[field] ? [...prev[field]] : [];
        newArray.push('');
        return { ...prev, [field]: newArray };
      });
    } else {
      setEditItem(prev => {
        const newArray = prev[field] ? [...prev[field]] : [];
        newArray.push('');
        return { ...prev, [field]: newArray };
      });
    }
  };

  const handleRemoveArrayItem = (field, index, isNew = false) => {
    if (isNew) {
      setNewItem(prev => {
        const newArray = [...prev[field]];
        newArray.splice(index, 1);
        return { ...prev, [field]: newArray };
      });
    } else {
      setEditItem(prev => {
        const newArray = [...prev[field]];
        newArray.splice(index, 1);
        return { ...prev, [field]: newArray };
      });
    }
  };

  const handleFileChange = (e) => {
    setFileUpload(e.target.files[0]);
  };

  const toggleBulkSelection = (id) => {
    setBulkSelection(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id) 
        : [...prev, id]
    );
  };

  const toggleAllBulkSelection = () => {
    if (bulkSelection.length === filteredItems.length) {
      setBulkSelection([]);
    } else {
      setBulkSelection(filteredItems.map(item => item.id));
    }
  };

  const renderFieldValue = (item, field) => {
    if (!item[field]) return '-';
    
    const fieldDef = selectedResource.fields?.[field];
    
    // Handle reference fields
    if (fieldDef?.type === 'reference') {
      const refCollection = referenceData[fieldDef.collection];
      if (refCollection) {
        const refItem = refCollection.find(r => r.id === item[field]);
        if (refItem) {
          return refItem[fieldDef.displayField] || refItem.id;
        }
      }
    }
    
    // Handle array of references
    if (fieldDef?.type === 'array' && fieldDef.itemType?.type === 'reference') {
      const refCollection = referenceData[fieldDef.itemType.collection];
      if (refCollection && Array.isArray(item[field])) {
        return item[field].map(id => {
          const refItem = refCollection.find(r => r.id === id);
          return refItem ? (refItem[fieldDef.itemType.displayField] || id) : id;
        }).join(', ');
      }
    }
    
    // Handle timestamps
    if (typeof item[field] === 'object' && item[field]?.seconds) {
      return new Date(item[field].seconds * 1000).toLocaleString();
    }
    
    // Handle nested objects
    if (typeof item[field] === 'object' && item[field] !== null) {
      return JSON.stringify(item[field]);
    }
    
    return String(item[field]);
  };

  const renderFieldInput = (field, value, isNew = false) => {
    if (!selectedResource.fields?.[field]) {
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleFieldChange(field, e.target.value, isNew)}
          className="w-full p-2 border rounded"
        />
      );
    }
    
    const fieldDef = selectedResource.fields[field];
    
    switch (fieldDef.type) {
      case 'string':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value, isNew)}
            className="w-full p-2 border rounded"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, Number(e.target.value), isNew)}
            className="w-full p-2 border rounded"
          />
        );
      
      case 'boolean':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value === 'true', isNew)}
            className="w-full p-2 border rounded"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );
      
      case 'timestamp':
        const dateValue = value?.seconds 
          ? new Date(value.seconds * 1000).toISOString().slice(0, 16)
          : '';
        return (
          <input
            type="datetime-local"
            value={dateValue}
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              handleFieldChange(field, date ? { seconds: Math.floor(date.getTime() / 1000) } : null, isNew);
            }}
            className="w-full p-2 border rounded"
          />
        );
      
      case 'reference':
        const refCollection = referenceData[fieldDef.collection] || [];
        let filteredRefs = refCollection;
        
        // Apply filter if defined
        if (fieldDef.filter) {
          const [filterField, op, filterValue] = fieldDef.filter;
          filteredRefs = refCollection.filter(item => {
            if (op === '==') return item[filterField] === filterValue;
            if (op === '!=') return item[filterField] !== filterValue;
            return true;
          });
        }
        
        return (
          <Combobox
            value={filteredRefs.find(r => r.id === value) || null}
            onChange={(selected) => handleFieldChange(field, selected?.id || '', isNew)}
          >
            <div className="relative">
              <Combobox.Input
                className="w-full p-2 border rounded"
                displayValue={(item) => 
                  item ? `${item[fieldDef.displayField]} (${item.id})` : ''
                }
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
              </Combobox.Button>
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {filteredRefs.map((item) => (
                  <Combobox.Option
                    key={item.id}
                    value={item}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {item[fieldDef.displayField]} ({item.id})
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <CheckIcon className="h-5 w-5" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </div>
          </Combobox>
        );
      
      case 'array':
        if (!Array.isArray(value)) value = [];
        
        if (fieldDef.itemType?.type === 'reference') {
          const refCollection = referenceData[fieldDef.itemType.collection] || [];
          let filteredRefs = refCollection;
          
          if (fieldDef.itemType.filter) {
            const [filterField, op, filterValue] = fieldDef.itemType.filter;
            filteredRefs = refCollection.filter(item => {
              if (op === '==') return item[filterField] === filterValue;
              if (op === '!=') return item[filterField] !== filterValue;
              return true;
            });
          }
          
          return (
            <div className="space-y-2">
              {value.map((itemId, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Combobox
                    value={filteredRefs.find(r => r.id === itemId) || null}
                    onChange={(selected) => handleArrayFieldChange(field, index, selected?.id || '', isNew)}
                  >
                    <div className="relative flex-1">
                      <Combobox.Input
                        className="w-full p-2 border rounded"
                        displayValue={(item) => 
                          item ? `${item[fieldDef.itemType.displayField]} (${item.id})` : ''
                        }
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                      </Combobox.Button>
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {filteredRefs.map((item) => (
                          <Combobox.Option
                            key={item.id}
                            value={item}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={`block truncate ${
                                    selected ? 'font-medium' : 'font-normal'
                                  }`}
                                >
                                  {item[fieldDef.itemType.displayField]} ({item.id})
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                  <button
                    onClick={() => handleRemoveArrayItem(field, index, isNew)}
                    className="p-2 text-red-500"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => handleAddArrayItem(field, isNew)}
                className="px-3 py-1 bg-gray-200 rounded text-sm"
              >
                + Add Item
              </button>
            </div>
          );
        } else {
          return (
            <div className="space-y-2">
              {value.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={item || ''}
                    onChange={(e) => handleArrayFieldChange(field, index, e.target.value, isNew)}
                    className="flex-1 p-2 border rounded"
                  />
                  <button
                    onClick={() => handleRemoveArrayItem(field, index, isNew)}
                    className="p-2 text-red-500"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => handleAddArrayItem(field, isNew)}
                className="px-3 py-1 bg-gray-200 rounded text-sm"
              >
                + Add Item
              </button>
            </div>
          );
        }
      
      case 'object':
        return (
          <textarea
            value={JSON.stringify(value || {}, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleFieldChange(field, parsed, isNew);
              } catch (err) {
                // Invalid JSON, don't update
              }
            }}
            className="w-full p-2 border rounded font-mono text-sm"
            rows={5}
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value, isNew)}
            className="w-full p-2 border rounded"
          />
        );
    }
  };

  const renderEditForm = () => {
    if (!editItem || !selectedResource.fields) return null;
    
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Edit {selectedResource.displayName}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(selectedResource.fields).map(([field, fieldDef]) => (
            <div key={field} className="space-y-1">
              <label className="block text-sm font-medium">
                {fieldDef.label || field}
              </label>
              {renderFieldInput(field, editItem[field], false)}
            </div>
          ))}
        </div>
        
        <div className="flex space-x-3 pt-4">
          <button
            onClick={handleSaveEdit}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Save Changes
          </button>
          <button
            onClick={() => setActiveTab('browse')}
            className="px-4 py-2 bg-gray-200 rounded"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderCreateForm = () => {
    if (!newItem || !selectedResource.fields) {
      if (selectedResource.type === 'storage') {
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Upload New {selectedResource.displayName}</h3>
            
            <div>
              <label className="block text-sm font-medium mb-1">File</label>
              <input
                type="file"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                onClick={handleSaveNew}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Upload File
              </button>
              <button
                onClick={() => setActiveTab('browse')}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        );
      }
      return null;
    }
    
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Create New {selectedResource.displayName}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(selectedResource.fields).map(([field, fieldDef]) => (
            <div key={field} className="space-y-1">
              <label className="block text-sm font-medium">
                {fieldDef.label || field}
              </label>
              {renderFieldInput(field, newItem[field], true)}
            </div>
          ))}
        </div>
        
        <div className="flex space-x-3 pt-4">
          <button
            onClick={handleSaveNew}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Create
          </button>
          <button
            onClick={() => setActiveTab('browse')}
            className="px-4 py-2 bg-gray-200 rounded"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderBrowseContent = () => {
    if (!selectedResource) return null;
    
    return (
      <>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 space-y-2 md:space-y-0">
          <h3 className="text-xl font-semibold">{selectedResource.displayName}</h3>
          
          <div className="flex space-x-2">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-1 border rounded"
            />
            
            <button
              onClick={handleCreate}
              className="px-3 py-1 bg-green-600 text-white rounded"
            >
              + New
            </button>
            
            {bulkSelection.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-600 text-white rounded"
              >
                Delete Selected ({bulkSelection.length})
              </button>
            )}
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className="p-2 border-b text-left">
                  <input
                    type="checkbox"
                    checked={bulkSelection.length === filteredItems.length && filteredItems.length > 0}
                    onChange={toggleAllBulkSelection}
                    className="form-checkbox h-4 w-4"
                  />
                </th>
                {selectedResource.fields && Object.keys(selectedResource.fields).map(field => (
                  <th
                    key={field}
                    className="p-2 border-b text-left"
                  >
                    {selectedResource.fields[field].label || field}
                  </th>
                ))}
                <th className="p-2 border-b text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={100} className="p-4 text-center text-gray-500">
                    No items found
                  </td>
                </tr>
              ) : (
                filteredItems.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="p-2 border-b">
                      <input
                        type="checkbox"
                        checked={bulkSelection.includes(item.id)}
                        onChange={() => toggleBulkSelection(item.id)}
                        className="form-checkbox h-4 w-4"
                      />
                    </td>
                    {selectedResource.fields && Object.keys(selectedResource.fields).map(field => (
                      <td key={field} className="p-2 border-b">
                        {renderFieldValue(item, field)}
                      </td>
                    ))}
                    <td className="p-2 border-b space-x-1">
                      <button
                        onClick={() => handleEdit(item)}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  if (loading) return <Preloader />;

  return (
    <div className="flex flex-col md:flex-row h-full bg-white">
      {/* Sidebar */}
      <nav className="w-full md:w-64 border-r p-4 bg-gray-50">
        <h2 className="text-lg font-semibold mb-4">Database Resources</h2>
        <ul className="space-y-1">
          {resources.map(resource => (
            <li key={`${resource.type}-${resource.name}`}>
              <button
                onClick={() => setSelectedResource(resource)}
                className={`w-full text-left py-2 px-3 rounded transition-all ${
                  selectedResource?.name === resource.name
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {resource.displayName || resource.name}
                <span className="text-xs ml-2 opacity-70">
                  ({resource.type})
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Main content */}
      <div className="flex-1 p-6 overflow-auto">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">
            {error}
          </div>
        )}
        
        {!selectedResource ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <p className="text-xl">Select a resource from the sidebar</p>
              <p className="mt-2">Choose a Firestore collection, Realtime Database path, or Storage folder to begin</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'browse' && renderBrowseContent()}
            {activeTab === 'edit' && renderEditForm()}
            {activeTab === 'create' && renderCreateForm()}
          </>
        )}
      </div>
    </div>
  );
};

export default ModifyDataTab;